/**
 * FERP Portal - Subgraph Entity Type Manager
 *
 * Management page for 100+ entity types per subgraph with:
 * - Preset-based progressive disclosure (solves IAM anti-pattern)
 * - Tabulator table with category row grouping
 * - Dynamic metadata columns per subgraph
 * - Tier-based enable/disable with essential type locking
 *
 * 3-Level Engagement:
 * - Zero-config: Default preset auto-applied
 * - Light-config: Pick from 3-5 presets (30-second decision)
 * - Fine-tuning: Toggle individual entity types in Tabulator
 *
 * FERP Principles:
 * - Data-driven: Categories, presets, tiers from backend metadata
 * - Dumb frontend: Displays what backend defines
 * - Progressive disclosure: Reduce cognitive load for 823 ERP entities
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Select,
  Tabs,
  Card,
  SimpleGrid,
  Alert,
  Loader,
  Center,
  ActionIcon,
  TextInput,
  Menu,
  ThemeIcon,
  Tooltip,
  Button,
} from '@mantine/core';
import {
  IconSearch,
  IconDownload,
  IconStack2,
  IconCheck,
  IconSettings,
  IconTable,
  IconLayoutGrid,
  IconRefresh,
  IconAlertTriangle,
  IconX,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth/AuthContext';
import { useTenantSubgraphConfig } from '@/hooks/useTenantSubgraphConfig';
import type { SubgraphInfo, SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import {
  useEntityTypeManager,
  TIER_DISPLAY,
  type ManagedEntityType,
} from '@/hooks/useEntityTypeManager';
import { InteractiveStatCard, type StatFilterScope } from '@/components/InteractiveStatCard';
import { useViewTracker } from '@/hooks/useViewTracker';
import { Tabulator, type CellComponent } from '@/types/tabulator';
import type { ColumnDefinition } from '@/types/tabulator';
import { TABULATOR_DEFAULTS, textColumn, badgeColumn } from '@/utils/tabulatorConfig';

import 'tabulator-tables/dist/css/tabulator.min.css';
import '@/styles/tabulator-mantine.css';

// ============================================================================
// Tier color mapping for badges
// ============================================================================

const TIER_COLORS: Record<string, string> = {
  essential: 'green',
  standard: 'blue',
  advanced: 'gray',
  specialist: 'violet',
};

const TIER_BADGE_CSS: Record<string, string> = {
  essential: 'critical',
  standard: 'normal',
  advanced: 'low',
  specialist: 'urgent',
};

// ============================================================================
// Preset Card Component
// ============================================================================

interface PresetCardProps {
  preset: { id: string; displayName: string; description: string; icon: string; entityCodes: string[]; isDefault: boolean };
  isActive: boolean;
  totalEntityCount: number;
  onApply: (presetId: string) => void;
}

function PresetCard({ preset, isActive, totalEntityCount, onApply }: PresetCardProps) {
  const entityCount = preset.entityCodes.length === 0 ? totalEntityCount : preset.entityCodes.length;

  return (
    <Card
      shadow={isActive ? 'md' : 'xs'}
      padding="lg"
      radius="md"
      withBorder
      style={{
        borderColor: isActive ? 'var(--mantine-color-blue-5)' : undefined,
        borderWidth: isActive ? 2 : 1,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
      onClick={() => onApply(preset.id)}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon variant="light" color={isActive ? 'blue' : 'gray'} size="md">
            <IconStack2 size={16} />
          </ThemeIcon>
          <Text fw={600} size="sm">{preset.displayName}</Text>
        </Group>
        <Group gap={4}>
          <Badge size="sm" variant="light">
            {preset.entityCodes.length === 0 ? 'ALL' : entityCount} types
          </Badge>
          {isActive && (
            <ThemeIcon variant="filled" color="blue" size="xs" radius="xl">
              <IconCheck size={10} />
            </ThemeIcon>
          )}
          {preset.isDefault && !isActive && (
            <Badge size="xs" variant="outline" color="gray">default</Badge>
          )}
        </Group>
      </Group>
      <Text size="xs" c="dimmed" lineClamp={2}>{preset.description}</Text>
    </Card>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

export default function SubgraphEntityManagerPage() {
  const tenantId = useTenantId();
  const { config, loading, error, getSubgraphConfig } = useTenantSubgraphConfig(tenantId ?? '');

  // Subgraph selection
  const [selectedSubgraphId, setSelectedSubgraphId] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<string | null>('presets');

  // Search state for entity types table
  const [searchTerm, setSearchTerm] = useState('');

  // Stat filter state (InteractiveStatCard clickable filtering)
  const [activeStatFilter, setActiveStatFilter] = useState<{
    category: string;
    scope: StatFilterScope;
  } | null>(null);

  // View tracker for "new" badges
  const viewTracker = useViewTracker('entity-type');

  // Tabulator ref
  const tableRef = useRef<HTMLDivElement>(null);
  const tableInstanceRef = useRef<InstanceType<typeof Tabulator> | null>(null);

  // Get subgraph options for the selector — use permission-based entity count
  const subgraphOptions = useMemo(() => {
    if (!config) return [];
    return config.subgraphs.map((sg) => {
      const sgConfig = getSubgraphConfig(sg.subgraphId);
      const count = sgConfig?.entityTypes.length ?? 0;
      return {
        value: sg.subgraphId,
        label: `${sg.displayName} (${count} types)`,
      };
    });
  }, [config, getSubgraphConfig]);

  // Auto-select first subgraph
  useEffect(() => {
    const first = subgraphOptions[0];
    if (first && !selectedSubgraphId) {
      setSelectedSubgraphId(first.value);
    }
  }, [subgraphOptions, selectedSubgraphId]);

  // Get selected subgraph config with properly filtered entity types
  // Uses permission-based matching (fhir:patient:read → fhir subgraph) + frontend fallback
  const selectedSubgraphConfig = useMemo(
    () => selectedSubgraphId ? getSubgraphConfig(selectedSubgraphId) : null,
    [selectedSubgraphId, getSubgraphConfig],
  );

  const selectedSubgraph: SubgraphInfo | null = selectedSubgraphConfig;
  const subgraphEntityTypes: SubgraphEntityType[] = selectedSubgraphConfig?.entityTypes ?? [];

  // Entity type manager hook
  const manager = useEntityTypeManager(selectedSubgraph, subgraphEntityTypes);

  // Build Tabulator columns
  const columns = useMemo((): ColumnDefinition[] => {
    const cols: ColumnDefinition[] = [
      {
        title: '',
        field: 'isEnabled',
        formatter: (cell: CellComponent) => {
          const data = cell.getRow().getData() as unknown as ManagedEntityType;
          if (data.isLocked) {
            return '<span title="Essential — always enabled" style="color: var(--mantine-color-green-6)">&#x1F512;</span>';
          }
          const checked = cell.getValue() ? 'checked' : '';
          return `<input type="checkbox" ${checked} class="ferp-entity-toggle" data-name="${data.name}" />`;
        },
        width: 50,
        hozAlign: 'center',
        headerSort: false,
        frozen: true,
      },
      {
        ...textColumn('name', { title: 'Entity Code', frozen: true, width: 180 }),
      },
      textColumn('pluralName', { title: 'Display Name', width: 200 }),
      badgeColumn('resolvedTier', TIER_BADGE_CSS, {
        title: 'Tier',
        width: 100,
        formatter: (cell: CellComponent) => {
          const val = cell.getValue() as string;
          const color = TIER_COLORS[val] ?? 'gray';
          const label = TIER_DISPLAY[val as keyof typeof TIER_DISPLAY] ?? val;
          return `<span class="ferp-tbl-badge ferp-tbl-badge--${TIER_BADGE_CSS[val] ?? 'normal'}">${label}</span>`;
        },
      }),
      {
        title: 'Compliance',
        field: 'complianceFrameworks',
        formatter: (cell: CellComponent) => {
          const frameworks = cell.getValue() as string[];
          if (!frameworks || frameworks.length === 0) return '';
          return frameworks
            .map((f) => `<span class="ferp-tbl-badge ferp-tbl-badge--normal">${f}</span>`)
            .join(' ');
        },
        width: 150,
        headerSort: false,
      },
      {
        title: 'Life-Critical',
        field: 'isLifeCritical',
        formatter: 'tickCross',
        width: 100,
        hozAlign: 'center',
      },
      textColumn('description', { width: 300 }),
    ];

    // Add dynamic metadata columns (hidden by default)
    for (const mc of manager.metadataColumns) {
      cols.push({
        title: mc.displayName,
        field: `metadata.${mc.key}`,
        visible: false, // Hidden by default, revealed via column toggle
        formatter: (cell: CellComponent) => {
          const val = cell.getValue();
          if (val === undefined || val === null) return '';
          if (typeof val === 'boolean') return val ? 'Yes' : 'No';
          return String(val);
        },
        width: 120,
      });
    }

    return cols;
  }, [manager.metadataColumns]);

  // Flatten managed types for Tabulator (resolve nested metadata)
  const tableData = useMemo(() => {
    return manager.managedTypes.map((mt) => {
      const row: Record<string, unknown> = { ...mt };
      // Flatten metadata into dot-notation fields for Tabulator
      if (mt.metadata) {
        for (const [key, value] of Object.entries(mt.metadata)) {
          row[`metadata.${key}`] = value;
        }
      }
      return row;
    });
  }, [manager.managedTypes]);

  // Initialize/update Tabulator
  useEffect(() => {
    if (!tableRef.current || activeTab !== 'entity-types' || tableData.length === 0) return;

    // Destroy existing instance
    tableInstanceRef.current?.destroy();

    const instance = new Tabulator(tableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: tableData,
      columns,
      groupBy: 'categoryDisplayName',
      groupStartOpen: true,
      groupHeader: (value: string, count: number) => {
        const group = manager.categoryGroups.find((g) => g.category.displayName === value);
        const enabledCount = group?.enabledCount ?? 0;
        return `<strong>${value}</strong> <span style="color: var(--mantine-color-dimmed)">(${enabledCount}/${count} enabled)</span>`;
      },
      height: '650px',
      paginationSize: 50,
    });

    // Handle checkbox clicks for toggle
    instance.on('cellClick', (_e: Event, cell: CellComponent) => {
      const field = cell.getField();
      if (field === 'isEnabled') {
        const data = cell.getRow().getData() as unknown as ManagedEntityType;
        if (!data.isLocked) {
          manager.toggleEntityType(data.name, !data.isEnabled);
        }
      }
    });

    tableInstanceRef.current = instance;

    return () => {
      tableInstanceRef.current?.destroy();
      tableInstanceRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tableData, columns]);

  // Apply stat filter to Tabulator (InteractiveStatCard clickable filtering)
  useEffect(() => {
    if (!tableInstanceRef.current) return;
    if (!activeStatFilter) {
      // Only clear if no search term active either
      if (!searchTerm) tableInstanceRef.current.clearFilter();
      return;
    }
    tableInstanceRef.current.setFilter((rowData: ManagedEntityType) => {
      let matchesCategory = true;
      switch (activeStatFilter.category) {
        case 'enabled': matchesCategory = rowData.isEnabled; break;
        case 'essential': matchesCategory = rowData.resolvedTier === 'essential'; break;
        case 'standard': matchesCategory = rowData.resolvedTier === 'standard'; break;
        case 'advanced': matchesCategory = rowData.resolvedTier === 'advanced'; break;
        case 'specialist': matchesCategory = rowData.resolvedTier === 'specialist'; break;
        case 'total': default: matchesCategory = true; break;
      }
      if (activeStatFilter.scope === 'new') {
        return matchesCategory && !viewTracker.isViewed(rowData.name);
      }
      return matchesCategory;
    });
  }, [activeStatFilter, viewTracker, searchTerm]);

  // Apply search filter
  useEffect(() => {
    const instance = tableInstanceRef.current;
    if (!instance) return;

    if (searchTerm) {
      instance.setFilter([
        [
          { field: 'name', type: 'like', value: searchTerm },
          { field: 'pluralName', type: 'like', value: searchTerm },
          { field: 'description', type: 'like', value: searchTerm },
        ],
      ] as unknown as Parameters<typeof instance.setFilter>[0]);
    } else if (!activeStatFilter) {
      instance.clearFilter();
    }
  }, [searchTerm, activeStatFilter]);

  // Export handler
  const handleExport = useCallback((format: 'csv' | 'json') => {
    const instance = tableInstanceRef.current;
    if (!instance) return;
    const ts = new Date().toISOString().split('T')[0];
    const sgId = selectedSubgraphId ?? 'unknown';
    instance.download(format, `entity-types-${sgId}_${ts}.${format}`);
  }, [selectedSubgraphId]);

  // ── Loading / Error states ────────────────────────────────────

  // Helper to set stat filter
  const handleStatClick = useCallback((category: string, scope: StatFilterScope) => {
    setActiveStatFilter((prev) =>
      prev?.category === category && prev?.scope === scope ? null : { category, scope },
    );
  }, []);

  // Count new (unviewed) entity types
  const newEntityCount = useMemo(
    () => subgraphEntityTypes.filter((et) => !viewTracker.isViewed(et.name)).length,
    [subgraphEntityTypes, viewTracker],
  );

  if (loading) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <Center h={400}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading entity type configurations...</Text>
          </Stack>
        </Center>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <Alert icon={<IconAlertTriangle size={16} />} title="Error" color="red">
          Failed to load subgraph configuration: {error.message}
        </Alert>
      </div>
    );
  }

  if (!config || config.subgraphs.length === 0) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <Alert icon={<IconSettings size={16} />} title="No Subgraphs" color="gray">
          No subgraphs are enabled for this tenant. Enable subgraphs from the Architect layer first.
        </Alert>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 32px' }}>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={2}>Entity Type Manager</Title>
            <Text size="sm" c="dimmed">
              Manage entity types per subgraph — presets, categories, and individual toggles
            </Text>
          </Stack>
          <Select
            label="Subgraph"
            placeholder="Select subgraph"
            value={selectedSubgraphId}
            onChange={setSelectedSubgraphId}
            data={subgraphOptions}
            style={{ minWidth: 280 }}
          />
        </Group>

        {/* Interactive stat cards (same pattern as TabulatorPrimitiveManager) */}
        {selectedSubgraph && (
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
            <InteractiveStatCard
              label="Total"
              total={manager.stats.totalTypes}
              newCount={newEntityCount}
              color="cyan"
              isActive={activeStatFilter?.category === 'total'}
              activeScope={activeStatFilter?.category === 'total' ? activeStatFilter.scope : null}
              onClickAll={() => handleStatClick('total', 'all')}
              onClickNew={() => handleStatClick('total', 'new')}
            />
            <InteractiveStatCard
              label="Enabled"
              total={manager.stats.enabledTypes}
              newCount={0}
              color="teal"
              isActive={activeStatFilter?.category === 'enabled'}
              activeScope={activeStatFilter?.category === 'enabled' ? activeStatFilter.scope : null}
              onClickAll={() => handleStatClick('enabled', 'all')}
              onClickNew={() => handleStatClick('enabled', 'new')}
            />
            <InteractiveStatCard
              label="Essential"
              total={manager.stats.essentialCount}
              newCount={0}
              color="green"
              isActive={activeStatFilter?.category === 'essential'}
              activeScope={activeStatFilter?.category === 'essential' ? activeStatFilter.scope : null}
              onClickAll={() => handleStatClick('essential', 'all')}
              onClickNew={() => handleStatClick('essential', 'new')}
            />
            <InteractiveStatCard
              label="Standard"
              total={manager.stats.standardCount}
              newCount={0}
              color="blue"
              isActive={activeStatFilter?.category === 'standard'}
              activeScope={activeStatFilter?.category === 'standard' ? activeStatFilter.scope : null}
              onClickAll={() => handleStatClick('standard', 'all')}
              onClickNew={() => handleStatClick('standard', 'new')}
            />
            <InteractiveStatCard
              label="Advanced"
              total={manager.stats.advancedCount}
              newCount={0}
              color="gray"
              isActive={activeStatFilter?.category === 'advanced'}
              activeScope={activeStatFilter?.category === 'advanced' ? activeStatFilter.scope : null}
              onClickAll={() => handleStatClick('advanced', 'all')}
              onClickNew={() => handleStatClick('advanced', 'new')}
            />
          </SimpleGrid>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="presets" leftSection={<IconLayoutGrid size={14} />}>
              Presets
            </Tabs.Tab>
            <Tabs.Tab value="entity-types" leftSection={<IconTable size={14} />}>
              Entity Types
              <Badge size="xs" variant="light" ml={6}>
                {manager.stats.enabledTypes}/{manager.stats.totalTypes}
              </Badge>
            </Tabs.Tab>
          </Tabs.List>

          {/* ── Presets Tab ──────────────────────────────────────── */}
          <Tabs.Panel value="presets" pt="md">
            <Stack gap="md">
              {manager.presets.length === 0 ? (
                <Alert color="gray" title="No Presets">
                  No presets defined for this subgraph. Use the Entity Types tab to manage individually.
                </Alert>
              ) : (
                <>
                  <Text size="sm" c="dimmed">
                    Pick a preset to quickly configure which entity types are active.
                    You can fine-tune individual types in the Entity Types tab.
                  </Text>
                  <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                    {manager.presets
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((preset) => (
                        <PresetCard
                          key={preset.id}
                          preset={preset}
                          isActive={manager.matchesPreset(preset.id)}
                          totalEntityCount={manager.stats.totalTypes}
                          onApply={manager.applyPreset}
                        />
                      ))}
                  </SimpleGrid>
                  {manager.activePreset && (
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">Active preset:</Text>
                      <Badge color="blue">{manager.activePreset.displayName}</Badge>
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>

          {/* ── Entity Types Tab (Tabulator) ─────────────────────── */}
          <Tabs.Panel value="entity-types" pt="md">
            <Stack gap="sm">
              {/* Toolbar */}
              <Group justify="space-between">
                <Group gap="xs" style={{ flex: 1 }}>
                  <TextInput
                    placeholder="Search entity types..."
                    leftSection={<IconSearch size={16} />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                    style={{ flex: 1, maxWidth: 350 }}
                  />
                  {activeStatFilter && (
                    <Badge
                      size="lg"
                      variant="light"
                      color="blue"
                      rightSection={
                        <ActionIcon size="xs" variant="transparent" onClick={() => setActiveStatFilter(null)}>
                          <IconX size={12} />
                        </ActionIcon>
                      }
                    >
                      {activeStatFilter.category} ({activeStatFilter.scope})
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<IconRefresh size={14} />}
                    onClick={manager.resetToDefault}
                  >
                    Reset to Default
                  </Button>
                  <Menu position="bottom-end" shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="light" aria-label="Export">
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => handleExport('csv')}>Download CSV</Menu.Item>
                      <Menu.Item onClick={() => handleExport('json')}>Download JSON</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Group>

              {/* Category quick toggles */}
              {manager.categoryGroups.length > 0 && (
                <Group gap="xs" wrap="wrap">
                  {manager.categoryGroups.map((group) => (
                    <Tooltip
                      key={group.category.id}
                      label={`${group.category.description} — ${group.enabledCount}/${group.totalCount} enabled`}
                    >
                      <Badge
                        size="sm"
                        variant={group.enabledCount === group.totalCount ? 'filled' : 'outline'}
                        color={group.enabledCount === group.totalCount ? 'blue' : 'gray'}
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          manager.toggleCategory(
                            group.category.id,
                            group.enabledCount < group.totalCount,
                          )
                        }
                      >
                        {group.category.displayName} ({group.enabledCount}/{group.totalCount})
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              )}

              {/* Tabulator table */}
              <div ref={tableRef} style={{ width: '100%' }} />

              {subgraphEntityTypes.length === 0 && (
                <Alert color="gray">
                  No entity types found for this subgraph. Entity types are defined in the subgraph configuration.
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </div>
  );
}
